import React, { useEffect, useMemo, useState } from 'react';
import { Briefcase, Users, ChevronRight, Sparkles, Search, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { candidatesAPI } from '../../lib/api';
import { CargoSummariesDialog } from './CargoSummariesDialog';

const tone = (total, max) => {
  const r = max ? total / max : 0;
  if (r >= 0.75) return { card: 'from-cyan-50 to-teal-50 border-cyan-300', icon: 'bg-cyan-700' };
  if (r >= 0.4) return { card: 'from-sky-50 to-cyan-50 border-sky-200', icon: 'bg-sky-600' };
  return { card: 'from-slate-50 to-white border-slate-200', icon: 'bg-slate-500' };
};

const CargoBanner = ({ g, max, onOpen }) => {
  const t = tone(g.total, max);
  const resumo = [g.com_disc && `${g.com_disc} com DISC`, g.pcd && `${g.pcd} PCD`, g.remoto && `${g.remoto} aceitam remoto`].filter(Boolean).join(' · ');
  return (
    <button type="button" onClick={() => onOpen(g)} data-testid={`cargo-banner-${g.key}`}
      className={`text-left rounded-2xl border bg-gradient-to-br ${t.card} p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform]`}>
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 shrink-0 rounded-xl ${t.icon} text-white flex items-center justify-center`}><Briefcase className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-slate-900 line-clamp-2 leading-tight" title={g.cargo}>{g.cargo}</p>
          {g.variacoes?.length > 0
            ? <p className="text-xs text-slate-500 line-clamp-2 mt-0.5" title={g.variacoes.join(', ')} data-testid={`cargo-banner-variacoes-${g.key}`}>Inclui: {g.variacoes.join(', ')}</p>
            : <p className="text-xs text-slate-500">Currículos disponíveis</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {g.novos_7d > 0 && (
            <span className="flex items-center gap-1 text-[10px] font-bold text-white bg-emerald-500 rounded-md px-1.5 py-0.5" data-testid={`cargo-banner-novos-${g.key}`}>
              <Sparkles className="w-3 h-3" />{g.novos_7d} novos
            </span>
          )}
          <span className="flex items-center gap-1 text-xs text-slate-600" data-testid={`cargo-banner-total-${g.key}`}><Users className="w-4 h-4" />{g.total}</span>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between rounded-lg border border-white bg-white/80 px-3 py-2 text-sm">
        <span className="text-slate-700">{g.total} {g.total === 1 ? 'currículo' : 'currículos'}</span>
        <span className="text-[11px] text-slate-500 truncate ml-2">{resumo || 'Ver resumos'}</span>
      </div>
    </button>
  );
};

export const CargoBannersTab = ({ onViewProfile }) => {
  const [data, setData] = useState(null);
  const [empresaOnly, setEmpresaOnly] = useState(false);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('total');
  const [open, setOpen] = useState(null);

  useEffect(() => {
    setData(null);
    candidatesAPI.byCargo(empresaOnly).then((r) => setData(r.data)).catch(() => { toast.error('Erro ao carregar cargos'); setData({ cargos: [], max_total: 0 }); });
  }, [empresaOnly]);

  const list = useMemo(() => {
    const items = (data?.cargos || []).filter((g) => !q || g.cargo.toLowerCase().includes(q.toLowerCase()));
    return sort === 'nome' ? [...items].sort((a, b) => a.cargo.localeCompare(b.cargo)) : items;
  }, [data, q, sort]);

  return (
    <div className="space-y-4" data-testid="cargo-banners-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrar cargo..." className="pl-9" data-testid="cargo-banners-search" />
        </div>
        <div className="flex rounded-lg border bg-white p-0.5 text-sm">
          {[['total', 'Mais currículos'], ['nome', 'A–Z']].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setSort(v)} data-testid={`cargo-sort-${v}`}
              className={`px-3 py-1.5 rounded-md ${sort === v ? 'bg-cyan-600 text-white' : 'text-slate-600'}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Switch id="empresa-only" checked={empresaOnly} onCheckedChange={setEmpresaOnly} data-testid="cargo-empresa-only" />
          <Label htmlFor="empresa-only" className="text-sm">Só cargos da minha empresa</Label>
        </div>
      </div>
      <p className="text-xs text-slate-500">Todos os candidatos cadastrados, exceto quem trabalha hoje na sua empresa. Abra o perfil para ver contato e baixar o currículo.</p>
      {data?.restrito_plano && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="cargo-plan-restricted">
          Seu plano exibe apenas candidatos que se candidataram às suas vagas. Para ver toda a base, solicite a busca global ao administrador.
        </p>
      )}
      {!data ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
      ) : list.length === 0 ? (
        <p className="py-12 text-center text-slate-500" data-testid="cargo-banners-empty">Nenhum cargo encontrado</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {list.map((g) => <CargoBanner key={g.key} g={g} max={data.max_total} onOpen={setOpen} />)}
        </div>
      )}
      <CargoSummariesDialog group={open} onClose={() => setOpen(null)} onViewProfile={onViewProfile} />
    </div>
  );
};
